import React, { useState, useEffect } from 'react';
import { useApi } from '../../hooks/useApi';
import { generateCodingTask, generateMCQ, validateGeneratedTask, getGeneratedTasks } from '../../services/api';
import { formatContent, formatInstructions, formatExpectedOutput } from '../../utils/contentFormatter';
import CodeEditor from '../CodeEditor/CodeEditor';
import McqScreen from './McqScreen';
import './ReflectionScreen.css';

const ReflectionScreen = ({ screen, onAnswer, userResponse, lessonId, subjectArea }) => {
  const { handleRequest } = useApi();
  const [reflection, setReflection] = useState(userResponse || '');
  const [generatedTasks, setGeneratedTasks] = useState({});
  const [showCodingTask, setShowCodingTask] = useState(false);
  const [showMCQ, setShowMCQ] = useState(false);
  const [loading, setLoading] = useState({ coding: false, mcq: false });
  const [code, setCode] = useState('');
  const [validationResult, setValidationResult] = useState(null);

  useEffect(() => {
    // Reset state when screen changes
    setGeneratedTasks({});
    setShowCodingTask(false);
    setShowMCQ(false);
    setCode('');
    setValidationResult(null);
    
    // Load existing generated tasks for this screen
    if (lessonId && screen.id) {
      handleRequest(() => getGeneratedTasks(lessonId, screen.id))
        .then(response => {
          const tasks = response.data.tasks || [];
          const tasksMap = {};
          tasks.forEach(t => {
            tasksMap[t.type] = t.task;
          });
          setGeneratedTasks(tasksMap);
          if (tasksMap.coding) setShowCodingTask(true);
          if (tasksMap.mcq) setShowMCQ(true);
        })
        .catch(err => console.error('Kunne ikke laste genererte oppgaver:', err));
    }
  }, [lessonId, screen.id, handleRequest]);

  const handleGenerateCodingTask = async () => {
    setLoading(prev => ({ ...prev, coding: true }));
    try {
      const response = await handleRequest(generateCodingTask, lessonId, { screenId: screen.id });
      setGeneratedTasks(prev => ({ ...prev, coding: response.data.task }));
      setShowCodingTask(true);
    } catch (error) {
      console.error('Kunne ikke generere kodeoppgave:', error);
    } finally {
      setLoading(prev => ({ ...prev, coding: false }));
    }
  };

  const handleGenerateMCQ = async () => {
    setLoading(prev => ({ ...prev, mcq: true }));
    try {
      const response = await handleRequest(generateMCQ, lessonId, { screenId: screen.id });
      setGeneratedTasks(prev => ({ ...prev, mcq: response.data.question }));
      setShowMCQ(true);
    } catch (error) {
      console.error('Kunne ikke generere flervalgsspørsmål:', error);
    } finally {
      setLoading(prev => ({ ...prev, mcq: false }));
    }
  };

  const handleSubmitCode = async () => {
    if (!code.trim()) return;
    setLoading(prev => ({ ...prev, coding: true }));
    try {
      const response = await handleRequest(validateGeneratedTask, lessonId, {
        screenId: screen.id,
        taskType: 'coding',
        answer: code
      });
      setValidationResult(response.data);
      if (onAnswer) {
        onAnswer(code, response.data);
      }
    } catch (error) {
      console.error('Kunne ikke validere kode:', error);
    } finally {
      setLoading(prev => ({ ...prev, coding: false }));
    }
  };

  const handleMCQAnswer = async (answer, result) => {
    if (onAnswer) {
      onAnswer(answer, result);
    }
  };

  const handleContinue = () => {
    if (!reflection.trim()) return;
    
    // Generate feedback for reflection
    const feedback = screen.checkingLogic?.feedbackCorrect || 
                     'Takk for refleksjonen din! Det er viktig å tenke over hvordan du kan bruke det du har lært.';
    
    if (onAnswer) {
      onAnswer(reflection, { 
        isCorrect: true, 
        feedback: feedback 
      });
    }
  };

  return (
    <div className="reflection-screen">
      <div className="reflection-content">
        <div className="reflection-panel">
          <h3 className="reflection-title">{screen.title}</h3>
          <p className="action-description">{screen.studentActionDescription}</p>
          
          <div 
            className="reflection-prompt"
            dangerouslySetInnerHTML={{ __html: formatContent(screen.contentForTeacherOrSystem) }}
          />

          <div className="reflection-input-area">
            <textarea
              value={reflection}
              onChange={(e) => setReflection(e.target.value)}
              placeholder="Share your thoughts..."
              rows="6"
              className="reflection-textarea"
            />
          </div>

          <button
            className="continue-button"
            onClick={handleContinue}
            disabled={!reflection.trim()}
          >
            {screen.uiLayoutHints?.primaryButtonLabel || 'Continue'}
          </button>
          
          {/* Show feedback after submission */}
          {userResponse && (
            <div className="reflection-feedback">
              <p>{screen.checkingLogic?.feedbackCorrect || 'Takk for refleksjonen din!'}</p>
            </div>
          )}

          {/* Generate Task Buttons */}
          {((subjectArea === 'programming' && !showCodingTask) || (!showMCQ && !generatedTasks.mcq)) && (
            <div className="generate-buttons">
              {subjectArea === 'programming' && !showCodingTask && (
                <button
                  className="generate-button generate-coding"
                  onClick={handleGenerateCodingTask}
                  disabled={loading.coding}
                >
                  {loading.coding ? 'Genererer...' : 'Generer kodeoppgave'}
                </button>
              )}
              {!showMCQ && !generatedTasks.mcq && (
                <button
                  className="generate-button generate-mcq"
                  onClick={handleGenerateMCQ}
                  disabled={loading.mcq}
                >
                  {loading.mcq ? 'Genererer...' : 'Generer flervalgsspørsmål'}
                </button>
              )}
            </div>
          )}

          {/* Generated Coding Task */}
          {subjectArea === 'programming' && showCodingTask && generatedTasks.coding && (
            <div className="generated-task coding-task">
              <h3>Kodeoppgave</h3>
              <p><strong>Beskrivelse:</strong> {generatedTasks.coding.description}</p>
              <p><strong>Instruksjoner:</strong> <span dangerouslySetInnerHTML={{ __html: formatInstructions(generatedTasks.coding.instructions) }} /></p>
              {generatedTasks.coding.expectedOutput && (
                <div className="expected-output-generated">
                  <strong>Forventet resultat:</strong>
                  <p dangerouslySetInnerHTML={{ __html: formatExpectedOutput(generatedTasks.coding.expectedOutput) }} />
                </div>
              )}
              <CodeEditor
                value={code}
                onChange={setCode}
                language="javascript"
                height="300px"
              />
              {validationResult && (
                <div className={`feedback ${validationResult.isCorrect ? 'feedback-correct' : 'feedback-incorrect'}`}>
                  {validationResult.feedback}
                </div>
              )}
              <button
                className="submit-code-button"
                onClick={handleSubmitCode}
                disabled={loading.coding || !code.trim()}
              >
                {loading.coding ? 'Validerer...' : 'Kjør kode'}
              </button>
            </div>
          )}

          {/* Generated MCQ */}
          {showMCQ && generatedTasks.mcq && (
            <div className="generated-task mcq-task">
              <McqScreen
                screen={generatedTasks.mcq}
                onAnswer={handleMCQAnswer}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReflectionScreen;
